import { ICON_SELECTOR } from '@/enum/Selectors';

/**
 * CSS selector for elements that should have font size adjusted
 */
const FONT_SIZE_SELECTOR = 'h1,h2,h3,h4,h5,h6,p,a,dl,dt,li,ol,th,td,span,blockquote,.cya11y-text,div,button';

/**
 * Set of icon selectors to exclude from font size adjustments
 */
const ICON_SELECTOR_SET = new Set(ICON_SELECTOR);

/**
 * Data attribute key for storing original font size
 */
const ORIGINAL_FONT_SIZE_ATTR = 'cya11yOrgFontSize';

/**
 * Data attribute key for storing original inline font size (if element had one before widget)
 */
const ORIGINAL_INLINE_FONT_SIZE_ATTR = 'cya11yOrgInlineFontSize';

/**
 * Adjusts font size of text elements on the page
 * @param multiply - Font size multiplier (default: 1)
 * @returns void
 */
export default function adjustFontSize(multiply: number = 1): void {
  try {
    if (typeof multiply !== 'number' || multiply <= 0) {
      console.warn('Invalid font size multiplier:', multiply);
      return;
    }

    const elements = document.querySelectorAll<HTMLElement>(FONT_SIZE_SELECTOR);
    
    elements.forEach((element: HTMLElement) => {
      try {
        // Skip elements that contain any class in ICON_SELECTOR_SET
        if (Array.from(element.classList).some(cls => ICON_SELECTOR_SET.has(cls))) {
          return;
        }

        // Store original font size and inline style BEFORE any widget modifications (first time only)
        const isFirstRun = !element.dataset[ORIGINAL_FONT_SIZE_ATTR];
        
        if (isFirstRun) {
          // Capture original inline style BEFORE widget touches it
          const originalInlineStyle = element.style.fontSize;
          const computedFontSize = parseInt(window.getComputedStyle(element).fontSize);
          
          // Validate font size
          if (isNaN(computedFontSize) || computedFontSize <= 0) {
            console.warn('Invalid original font size for element:', element);
            return;
          }
          
          // Store original computed font size
          element.dataset[ORIGINAL_FONT_SIZE_ATTR] = String(computedFontSize);
          
          // Store original inline style if it existed (from website, not widget)
          if (originalInlineStyle) {
            element.dataset[ORIGINAL_INLINE_FONT_SIZE_ATTR] = originalInlineStyle;
          }
        }

        // Get stored original font size
        const originalFontSize = Number(element.dataset[ORIGINAL_FONT_SIZE_ATTR]);
        
        // Validate stored font size
        if (isNaN(originalFontSize) || originalFontSize <= 0) {
          console.warn('Invalid stored original font size for element:', element);
          return;
        }

        // If multiply is 1 (100%), only restore if widget has previously modified this element
        if (multiply === 1) {
          // On first run at 100%, do nothing (don't touch elements widget hasn't modified)
          if (isFirstRun) {
            return;
          }
          // On subsequent runs at 100%, restore original state
          if (element.dataset[ORIGINAL_INLINE_FONT_SIZE_ATTR]) {
            element.style.fontSize = element.dataset[ORIGINAL_INLINE_FONT_SIZE_ATTR];
          } else {
            element.style.fontSize = '';
          }
          return;
        }

        // Calculate and apply new font size
        const newFontSize = originalFontSize * multiply;
        element.style.fontSize = `${newFontSize.toFixed(0)}px`;
      } catch (elementError) {
        console.warn('Failed to adjust font size for element:', element, elementError);
      }
    });
  } catch (error) {
    console.error('Failed to adjust font size:', error);
  }
}